import { useState } from "react"
import type { z } from "zod"
import { toast } from "sonner"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { ErrorState, LoadingState } from "~/components/screens/StateScreen"
import { FormDiscordFields } from "~/components/forms/FormDiscordFields"
import type { formDiscordSettingsSchema } from "~/lib/schemas"
import { api } from "~/trpc/react"

export function FormDiscordSettings({
	teamId,
	formId
}: {
	teamId: string
	formId?: string
}) {
	const settings = api.discord.formResponseSettings.useQuery({ teamId, formId })
	const utils = api.useUtils()
	const [draft, setDraft] = useState<{
		value: z.infer<typeof formDiscordSettingsSchema>
		updatedAt: Date
	} | null>(null)
	const saved = {
		mode:
			settings.data?.mode ?? (formId ? ("inherit" as const) : ("off" as const)),
		guildId: settings.data?.channel?.guildId ?? "",
		channelId: settings.data?.channel?.channelId ?? ""
	}
	const value = draft?.value ?? saved
	const guilds = api.discord.guilds.useQuery(
		{ teamId },
		{ enabled: value.mode === "channel", retry: false }
	)
	const channels = api.discord.guildChannels.useQuery(
		{ teamId, guildId: value.guildId },
		{ enabled: value.mode === "channel" && !!value.guildId, retry: false }
	)
	const available =
		channels.data?.flatMap((category) =>
			category.channels
				.filter((channel) => channel.canSendFormResponses)
				.map((channel) => ({
					id: channel.id,
					name: category.name
						? `${category.name} / ${channel.name}`
						: channel.name
				}))
		) ?? []
	const save = api.discord.setFormResponseChannel.useMutation({
		onSuccess: async () => {
			await utils.discord.formResponseSettings.invalidate()
			setDraft(null)
			toast.success("Discord response settings saved")
		}
	})
	if (settings.isLoading) return <LoadingState scope="inline" />
	if (settings.error || !settings.data)
		return (
			<ErrorState
				title="Unable to load Discord settings"
				description={settings.error?.message}
			/>
		)
	const current = settings.data
	const dirty = JSON.stringify(value) !== JSON.stringify(saved)
	const channelMissing =
		value.mode === "channel" &&
		!channels.isLoading &&
		!channels.error &&
		!!value.guildId &&
		available.length === 0
	return (
		<div className="flex flex-col gap-4">
			<FormDiscordFields
				value={value}
				onChange={(next) =>
					setDraft({
						value: next,
						updatedAt: draft?.updatedAt ?? current.updatedAt
					})
				}
				perForm={!!formId}
				teamDefaultLabel={current.teamDefaultLabel}
				guilds={guilds.data ?? []}
				channels={available}
				loadingGuilds={guilds.isLoading}
				loadingChannels={channels.isLoading}
				disabled={save.isPending}
			/>
			{value.mode === "channel" && (
				<>
					{(guilds.error || channels.error) && (
						<p role="alert" className="text-sm text-destructive">
							{guilds.error?.message ?? channels.error?.message}
						</p>
					)}
					{guilds.data?.length === 0 && (
						<p className="text-sm text-muted-foreground">
							Connect Discord in account settings and install Orbit on a server
							you manage.
						</p>
					)}
					{channelMissing && (
						<p className="text-sm text-muted-foreground">
							Orbit needs View Channel, Send Messages, Embed Links, and Read
							Message History in a text or announcement channel.
						</p>
					)}
					{value.channelId &&
						!channels.isLoading &&
						!available.some((channel) => channel.id === value.channelId) && (
							<p role="alert" className="text-sm text-destructive">
								The configured channel is unavailable. Choose another channel or
								turn delivery off.
							</p>
						)}
					<p className="text-sm text-muted-foreground">
						New responses, including answers and file links, will be visible to
						everyone who can read this channel.
					</p>
				</>
			)}
			<UnsavedChanges
				isVisible={dirty}
				pending={save.isPending}
				canSave={
					value.mode !== "channel" ||
					available.some((channel) => channel.id === value.channelId)
				}
				onReset={async () => setDraft(null)}
				onSave={async () => {
					await save.mutateAsync({
						teamId,
						formId,
						...value,
						updatedAt: draft?.updatedAt ?? current.updatedAt
					})
				}}
			/>
		</div>
	)
}
